import { ChecklistTemplate } from './types';

/**
 * Plantilla de revisión para Sistema de Evacuación de Humos por SOBREPRESIÓN.
 *
 * Referencias normativas:
 *   - RD 513/2017 (RIPCI), Anexo II.
 *   - UNE-EN 12101-6 (sistemas de control de humo y calor — diferencial de presión).
 *   - UNE 23585 / CTE DB-SI.
 *
 * Periodicidad: T = Trimestral · A = Anual.
 */
export const SOBREPRESION_TEMPLATE: ChecklistTemplate = {
  tipo_sistema: 'pci_sobrepresion',
  titulo: 'Evacuación de humos (Sobrepresión)',
  protocolos: [
    {
      id: 'inventario',
      titulo: '1. Inventario y configuración general',
      cabecera: [
        { key: 'sector_protegido', label: 'Sector / escalera protegida' },
        { key: 'clase_sistema', label: 'Clase sistema EN 12101-6 (A–F)' },
        { key: 'presion_diseno', label: 'Presión diferencial de diseño (Pa)' },
        { key: 'caudal_diseno', label: 'Caudal de aire de diseño (m³/h)' },
      ],
      preguntas: [
        { id: 'IN.01', texto: '¿El inventario físico coincide con planos y proyecto técnico?', periodicidad: ['A'] },
        { id: 'IN.02', texto: '¿Existe esquema de la instalación visible en sala técnica?', periodicidad: ['A'] },
        { id: 'IN.03', texto: '¿La presión diferencial proyectada (≥ 50 Pa puerta cerrada) está documentada?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'central',
      titulo: '2. Central de control de sobrepresión',
      cabecera: [
        { key: 'marca_central', label: 'Marca central' },
        { key: 'modelo_central', label: 'Modelo central' },
        { key: 'tension_alimentacion', label: 'Tensión alimentación (V)' },
        { key: 'baterias_v', label: 'Baterías (V/Ah)' },
        { key: 'fecha_baterias', label: 'Fecha baterías', tipo: 'date' },
      ],
      preguntas: [
        { id: 'CE.01', texto: '¿La central está alimentada y sin avisos de avería ni desconexión?', periodicidad: ['T','A'] },
        { id: 'CE.02', texto: '¿Los indicadores luminosos y zumbador funcionan correctamente?', periodicidad: ['T','A'] },
        { id: 'CE.03', texto: '¿Se ha probado la conmutación a baterías cortando la red 230 V?', periodicidad: ['A'] },
        { id: 'CE.04', texto: '¿La autonomía cumple con lo exigido por proyecto / fabricante?', periodicidad: ['A'] },
        { id: 'CE.05', texto: '¿La señal de activación desde la central de incendios (CDI) llega correctamente?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'ventiladores',
      titulo: '3. Ventiladores de sobrepresión',
      cabecera: [
        { key: 'num_ventiladores', label: 'Nº ventiladores', tipo: 'number' },
        { key: 'caudal_nominal', label: 'Caudal nominal (m³/h)' },
        { key: 'clase_400_2h', label: 'Clase F (400 ºC / 2 h)' },
      ],
      preguntas: [
        { id: 'VE.01', texto: '¿El ventilador arranca correctamente y alcanza el régimen nominal?', periodicidad: ['T','A'], unidades: true },
        { id: 'VE.02', texto: '¿Los amortiguadores, correas y rodamientos están en buen estado y sin vibraciones anómalas?', periodicidad: ['A'], unidades: true },
        { id: 'VE.03', texto: '¿La placa de características es legible y la marca CE / clasificación al fuego es conforme?', periodicidad: ['A'], unidades: true },
        { id: 'VE.04', texto: '¿La rejilla de aspiración / impulsión está libre de obstáculos y suciedad?', periodicidad: ['T','A'], unidades: true },
        { id: 'VE.05', texto: '¿El consumo eléctrico medido coincide con el de placa (± tolerancia)?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'compresor',
      titulo: '4. Compresor y red neumática (si procede)',
      cabecera: [
        { key: 'tension_compresor', label: 'Tensión (V)' },
        { key: 'potencia_compresor', label: 'Potencia (kW)' },
        { key: 'rpm_compresor', label: 'RPM' },
        { key: 'caudal_compresor', label: 'Caudal (l/min)' },
        { key: 'presion_servicio', label: 'Presión servicio (bar)' },
      ],
      preguntas: [
        { id: 'CP.01', texto: '¿El compresor arranca y para automáticamente dentro de los rangos de presión configurados?', periodicidad: ['T','A'] },
        { id: 'CP.02', texto: '¿El nivel / condición del aceite (si procede) y filtros están en buen estado?', periodicidad: ['A'] },
        { id: 'CP.03', texto: '¿Se ha purgado el calderín y el separador de condensados?', periodicidad: ['T','A'] },
        { id: 'CP.04', texto: '¿La red de aire comprimido está sin fugas (test de caída de presión)?', periodicidad: ['A'] },
        { id: 'CP.05', texto: '¿Tensión, potencia, RPM y caudal coinciden con la placa de características?', periodicidad: ['A'] },
        { id: 'CP.06', texto: '¿El presostato y la válvula de seguridad funcionan correctamente?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'compuertas',
      titulo: '5. Compuertas de sobrepresión / alivio',
      preguntas: [
        { id: 'CM.01', texto: '¿La compuerta de alivio abre al alcanzar la presión consigna y cierra al normalizarse?', periodicidad: ['A'], unidades: true },
        { id: 'CM.02', texto: '¿Los actuadores (24 V / 230 V) reciben tensión y maniobran correctamente?', periodicidad: ['A'], unidades: true },
        { id: 'CM.03', texto: '¿Las lamas / hojas están limpias, sin deformaciones y se mueven con suavidad?', periodicidad: ['T','A'], unidades: true },
        { id: 'CM.04', texto: '¿La estanqueidad en reposo es correcta?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'sensores',
      titulo: '6. Sensores de presión diferencial',
      preguntas: [
        { id: 'SP.01', texto: '¿El sensor está instalado en el punto correcto (recinto presurizado vs zona adyacente)?', periodicidad: ['A'], unidades: true },
        { id: 'SP.02', texto: '¿La lectura en reposo está dentro del rango esperado?', periodicidad: ['T','A'], unidades: true },
        { id: 'SP.03', texto: '¿El sensor está calibrado / verificado según la periodicidad del fabricante?', periodicidad: ['A'], unidades: true },
        { id: 'SP.04', texto: '¿Las tomas de presión y mangueras están libres de obstrucciones?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'maniobra',
      titulo: '7. Maniobra y prueba de funcionamiento',
      preguntas: [
        { id: 'MN.01', texto: '¿La maniobra desde alarma de incendios activa el sistema en ≤ 60 s?', periodicidad: ['A'] },
        { id: 'MN.02', texto: '¿La presión diferencial alcanzada con todas las puertas cerradas es ≥ 50 Pa?', periodicidad: ['A'] },
        { id: 'MN.03', texto: '¿La fuerza de apertura de la puerta de evacuación es ≤ 100 N (EN 12101-6)?', periodicidad: ['A'] },
        { id: 'MN.04', texto: '¿La velocidad de aire en puerta abierta cumple con la clase del sistema (≥ 0,75 / 1 / 2 m/s)?', periodicidad: ['A'] },
        { id: 'MN.05', texto: '¿La maniobra por pulsador manual y el RESET funcionan correctamente?', periodicidad: ['T','A'] },
      ],
    },
    {
      id: 'documentacion',
      titulo: '8. Documentación y registros',
      preguntas: [
        { id: 'DC.01', texto: '¿Existe acta firmada de la última revisión anual por empresa mantenedora habilitada?', periodicidad: ['A'] },
        { id: 'DC.02', texto: '¿Está actualizado el inventario con marca, modelo, ubicación y NºSerie?', periodicidad: ['A'] },
        { id: 'DC.03', texto: '¿Se conservan los certificados de prueba de presión y caudal del sistema?', periodicidad: ['A'] },
        { id: 'DC.04', texto: '¿Se ha entregado al titular el informe de anomalías y acciones correctoras?', periodicidad: ['A'] },
      ],
    },
  ],
};
